import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { List, PlusCircle, LogOut, Menu, Bell, User, MapPin, Package, ChevronLeft, X, Sun, Moon, Receipt } from 'lucide-react';
import { useState, useRef } from 'react';
import { useSignOut } from '../features/auth/hooks';
import { useAppStore } from '../store/useAppStore';
import { ToastProvider } from '../shared/ui/Toast';
import { AnimateIn } from '../shared/ui/AnimateIn';
import { useRealtimeNotifications, useNotifications } from '../features/notifications/hooks';
import { ConfirmModal } from '../shared/ui/ConfirmModal';
import { OnboardingModal } from '../shared/ui/OnboardingModal';
import { CompleteProfileModal } from '../shared/ui/CompleteProfileModal';
import { Button } from '../shared/ui/Button';
import { useDarkMode } from '../shared/hooks/useDarkMode';
import { useScrollToTop } from '../shared/hooks/useScrollToTop';

const sidebarRowCls =
  'w-full !justify-start gap-3 !px-3 !py-2 text-sm normal-case !tracking-normal rounded-xl !text-white/30 hover:!bg-white/5';
const sidebarRowCollapsedCls =
  'w-full !justify-center gap-3 !px-3 !py-2 text-sm normal-case !tracking-normal rounded-xl !text-white/30 hover:!bg-white/5';

const navItems = [
  { to: '/app/requests', label: 'My Requests', icon: List },
  { to: '/app/requests/new', label: 'New Request', icon: PlusCircle },
  { to: '/app/saved-locations', label: 'Saved Locations', icon: MapPin },
  { to: '/app/saved-equipment', label: 'Saved Equipment', icon: Package },
  { to: '/app/invoices', label: 'My Invoices', icon: Receipt },
  { to: '/app/notifications', label: 'Notifications', icon: Bell },
  { to: '/app/profile', label: 'Profile', icon: User },
];

export function PortalLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarClosing, setSidebarClosing] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const { dark, toggle: toggleDark } = useDarkMode();
  const mainRef = useRef<HTMLElement>(null);
  useScrollToTop(mainRef);

  const openSidebar = () => { setSidebarClosing(false); setSidebarOpen(true); };
  const closeSidebar = () => {
    setSidebarClosing(true);
    setTimeout(() => { setSidebarOpen(false); setSidebarClosing(false); }, 220);
  };
  const [signOutOpen, setSignOutOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const user = useAppStore((s) => s.user);
  const signOut = useSignOut();
  const navigate = useNavigate();
  useRealtimeNotifications();
  // Filtered client-side (not just trusting the server's `unreadOnly` query param) so a future
  // backend contract mismatch degrades to an over-inclusive count rather than a silently stuck one.
  const { data: notifications = [] } = useNotifications({ unreadOnly: true });
  const unreadCount = notifications.filter((n) => !n.read).length;

  const handleSignOut = async () => {
    setSigningOut(true);
    await signOut();
    navigate('/login');
  };

  return (
    <ToastProvider>
      <div className="flex h-screen bg-bg overflow-hidden">
        {/* Desktop sidebar */}
        <aside
          className={`hidden lg:flex flex-col border-r border-white/10 bg-[#0a0a0a] transition-all duration-200 ${collapsed ? 'w-16' : 'w-56'}`}
        >
          <div className={`flex items-center border-b border-white/10 px-3 py-4 ${collapsed ? 'justify-center' : 'justify-between'}`}>
            {!collapsed && (
              <img src="/logo-white.png" alt="RT Equipment & Haulage" className="h-8 w-auto" />
            )}
            {collapsed && (
              <img src="/logo-white.png" alt="RT" className="h-7 w-7 object-contain" />
            )}
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => setCollapsed((v) => !v)}
              className="!text-muted hover:!text-text hover:!bg-transparent"
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              <ChevronLeft size={16} className={`transition-transform ${collapsed ? 'rotate-180' : ''}`} />
            </Button>
          </div>

          <nav className="flex-1 p-2 flex flex-col gap-1">
            {navItems.map(({ to, label, icon: Icon }, i) => (
              <AnimateIn key={to} animation="slide-left" delay={i * 45}>
              <NavLink
                to={to}
                end={to !== '/app/requests/new'}
                className={({ isActive }) =>
                  `group flex items-center gap-3 px-3 py-2.5 text-sm rounded-xl transition-all hover:translate-x-0.5 ${isActive ? 'bg-white/10 text-accent font-semibold' : 'text-white/40 hover:bg-white/5 hover:text-white'} ${collapsed ? 'justify-center' : ''}`
                }
                title={collapsed ? label : undefined}
              >
                <span className="relative flex-shrink-0">
                  <Icon size={18} className="transition-transform duration-150 group-hover:scale-110" />
                  {to === '/app/notifications' && unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-accent" />
                  )}
                </span>
                <span className={`overflow-hidden whitespace-nowrap transition-all duration-200 ${collapsed ? 'max-w-0 opacity-0' : 'max-w-[160px] opacity-100'}`}>
                  {label}
                </span>
                {!collapsed && to === '/app/notifications' && unreadCount > 0 && (
                  <span className="ml-auto flex-shrink-0 min-w-[18px] h-[18px] px-1 rounded-full bg-accent text-black text-[10px] font-bold flex items-center justify-center">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </NavLink>
              </AnimateIn>
            ))}
          </nav>

          <div className="border-t border-white/10 p-2">
            {!collapsed && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/5 mb-1">
                {user?.avatarUrl ? (
                  <img src={user.avatarUrl} alt="" className="h-7 w-7 rounded-full object-cover flex-shrink-0" />
                ) : (
                  <div className="h-7 w-7 rounded-full bg-accent flex items-center justify-center text-black text-xs font-bold flex-shrink-0">
                    {user?.avatarInitials ?? 'U'}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-white truncate">{user?.name}</p>
                  <p className="text-xs text-white/30">Customer</p>
                </div>
              </div>
            )}
            <Button
              variant="ghost"
              onClick={toggleDark}
              className={`${collapsed ? sidebarRowCollapsedCls : sidebarRowCls} hover:!text-white`}
              title={collapsed ? (dark ? 'Light mode' : 'Dark mode') : undefined}
              aria-label={dark ? 'Light mode' : 'Dark mode'}
            >
              {dark ? <Sun size={16} /> : <Moon size={16} />}
              {!collapsed && (dark ? 'Light mode' : 'Dark mode')}
            </Button>
            <Button
              variant="ghost"
              onClick={() => setSignOutOpen(true)}
              className={`${collapsed ? sidebarRowCollapsedCls : sidebarRowCls} hover:!text-red-400`}
              title={collapsed ? 'Sign out' : undefined}
              aria-label="Sign out"
            >
              <LogOut size={16} />
              {!collapsed && 'Sign out'}
            </Button>
          </div>
        </aside>

        {/* Mobile sidebar overlay */}
        {sidebarOpen && (
          <>
            <div
              className={`fixed inset-0 z-20 bg-black/40 lg:hidden ${sidebarClosing ? 'animate-scrim-out' : 'animate-scrim-in'}`}
              onClick={closeSidebar}
            />
            <aside className={`fixed inset-y-0 left-0 z-30 w-60 flex flex-col border-r border-white/10 bg-[#0a0a0a] lg:hidden ${sidebarClosing ? 'animate-sidebar-out' : 'animate-sidebar-in'}`}>
              <div className="flex items-center justify-between border-b border-white/10 px-4 py-4">
                <img src="/logo-white.png" alt="RT Equipment & Haulage" className="h-8 w-auto" />
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={closeSidebar}
                  className="!text-white/40 hover:!text-white hover:!bg-transparent"
                  aria-label="Close menu"
                >
                  <X size={18} />
                </Button>
              </div>
              <nav className="flex-1 p-3 flex flex-col gap-1">
                {navItems.map(({ to, label, icon: Icon }, i) => (
                  <AnimateIn key={to} animation="slide-left" delay={i * 40}>
                  <NavLink
                    to={to}
                    end={to !== '/app/requests/new'}
                    onClick={closeSidebar}
                    className={({ isActive }) =>
                      `group flex items-center gap-3 px-3 py-2.5 text-sm rounded-xl transition-all hover:translate-x-0.5 ${isActive ? 'bg-white/10 text-accent font-semibold' : 'text-white/40 hover:bg-white/5 hover:text-white'}`
                    }
                  >
                    <Icon size={18} className="flex-shrink-0 transition-transform duration-150 group-hover:scale-110" />
                    {label}
                    {to === '/app/notifications' && unreadCount > 0 && (
                      <span className="ml-auto flex-shrink-0 min-w-[18px] h-[18px] px-1 rounded-full bg-accent text-black text-[10px] font-bold flex items-center justify-center">
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    )}
                  </NavLink>
                  </AnimateIn>
                ))}
              </nav>
              <div className="border-t border-white/10 p-3">
                <Button
                  variant="ghost"
                  onClick={toggleDark}
                  className={`${sidebarRowCls} hover:!text-white`}
                  aria-label={dark ? 'Light mode' : 'Dark mode'}
                >
                  {dark ? <Sun size={16} /> : <Moon size={16} />}
                  {dark ? 'Light mode' : 'Dark mode'}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => { closeSidebar(); setSignOutOpen(true); }}
                  className={`${sidebarRowCls} hover:!text-red-400`}
                  aria-label="Sign out"
                >
                  <LogOut size={16} />
                  Sign out
                </Button>
              </div>
            </aside>
          </>
        )}

        {/* Main */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <header className="flex items-center justify-between border-b border-line px-4 py-3 lg:hidden">
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={openSidebar}
              className="!text-text-secondary hover:!bg-transparent"
              aria-label="Open menu"
            >
              <Menu size={22} />
            </Button>
            <img src={dark ? '/logo-white.png' : '/logo.png'} alt="RT" className="h-7 w-auto" />
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={toggleDark}
                className="!text-text-secondary hover:!bg-transparent"
                aria-label={dark ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {dark ? <Sun size={20} /> : <Moon size={20} />}
              </Button>
              <Link to="/app/notifications" className="relative text-text-secondary" aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}>
                <Bell size={20} />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-accent border-2 border-bg" />
                )}
              </Link>
            </div>
          </header>

          <main ref={mainRef} className="flex-1 overflow-y-auto px-4 pt-4 pb-6 md:px-6 md:pt-6 md:pb-6 lg:p-6">
            <Outlet />
          </main>
        </div>
      </div>

      <ConfirmModal
        open={signOutOpen}
        onClose={() => setSignOutOpen(false)}
        onConfirm={handleSignOut}
        title="Sign out?"
        message={`You're signed in as ${user?.name ?? 'Customer'}. You'll be returned to the login screen.`}
        confirmLabel="Sign out"
        danger
        loading={signingOut}
      />

      <CompleteProfileModal open={!!user && !user.phone} />
      <OnboardingModal open={!!user && !!user.phone && !user.onboardingCompleted} />
    </ToastProvider>
  );
}
